import { html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import '@awesome.me/webawesome/dist/components/format-date/format-date.js';
import '@awesome.me/webawesome/dist/components/relative-time/relative-time.js';
import '@awesome.me/webawesome/dist/components/format-number/format-number.js';

import {
  DATE_PRESETS,
  feedTimePreset,
  formatDate,
  formatMoney,
  isCurrencyCode,
  toValidDate,
  type DateInput,
  type DatePreset,
  type MoneyInput,
  type MoneyOptions,
} from './format.js';

// These sit inside sentences, so their templates carry no surrounding whitespace.
export function renderDate(value: DateInput, preset: DatePreset, fallback = '') {
  const date = toValidDate(value);
  if (!date) return fallback;
  const options: Intl.DateTimeFormatOptions = DATE_PRESETS[preset];
  // prettier-ignore
  return html`<wa-format-date
    .date=${date}
    year=${ifDefined(options.year)}
    month=${ifDefined(options.month)}
    day=${ifDefined(options.day)}
    hour=${ifDefined(options.hour)}
    minute=${ifDefined(options.minute)}
  ></wa-format-date>`;
}

export function renderMoney(
  amount: MoneyInput,
  currency: string | null | undefined,
  { wholeUnits = false }: MoneyOptions = {},
) {
  if (!isCurrencyCode(currency)) return formatMoney(amount, currency, { wholeUnits });
  // prettier-ignore
  return html`<wa-format-number
    type="currency"
    currency=${currency}
    .value=${Number(amount ?? 0)}
    maximum-fraction-digits=${ifDefined(wholeUnits ? 0 : undefined)}
  ></wa-format-number>`;
}

export function renderRelativeDate(value: DateInput, fallback = '') {
  const date = toValidDate(value);
  if (!date) return fallback;
  // prettier-ignore
  return html`<wa-relative-time .date=${date}></wa-relative-time>`;
}

// Today shows the time, older entries the day; the label carries the full value for assistive tech.
export function renderFeedTime(value: DateInput, { className, part }: { className?: string; part?: string } = {}) {
  const date = toValidDate(value);
  if (!date) return nothing;
  return html`
    <time
      part=${ifDefined(part)}
      class=${ifDefined(className)}
      datetime=${date.toISOString()}
      aria-label=${formatDate(date, 'dateTime')}
    >
      ${renderDate(date, feedTimePreset(date))}
    </time>
  `;
}
